/**
 * Widget UI — Vanilla JS / TypeScript
 * Renderiza el botón flotante + ventana de chat.
 * Usa textContent (nunca innerHTML) para evitar XSS.
 */

import { getOrCreateSession, saveSession } from './session'
import { sendMessage } from './sse-client'

interface AgentConfig {
  name: string
  botDisplayName: string
  welcomeMessage: string | null
  colorPrimary: string
}

// ─────────────────────────────────────────────────────
// Estado del widget
// ─────────────────────────────────────────────────────
let isOpen = false
let sessionId: string | null = null
let conversationId: string | null = null
let apiBase = ''

// ─────────────────────────────────────────────────────
// Helpers DOM
// ─────────────────────────────────────────────────────
function el<T extends HTMLElement>(tag: string, cls?: string): T {
  const e = document.createElement(tag) as T
  if (cls) e.className = cls
  return e
}

// ─────────────────────────────────────────────────────
// Safe text → HTML (preserves line breaks, no XSS)
// ─────────────────────────────────────────────────────
function escapeHtml(text: string): string {
  const div = document.createElement('div')
  div.textContent = text
  return div.innerHTML
}

function formatMessage(text: string): string {
  const safe = escapeHtml(text)
  // Convierte URLs en enlaces clickeables
  return safe.replace(
    /(https?:\/\/[^\s<]+)/g,
    '<a href="$1" target="_blank" rel="noopener noreferrer" style="color:#7eb8f7;text-decoration:underline;word-break:break-all;">$1</a>'
  )
}

function injectStyles(color: string) {
  const css = `
    #cw-bubble {
      position: fixed; bottom: 24px; right: 24px; z-index: 99998;
      width: 56px; height: 56px; border-radius: 50%;
      background: ${color}; border: none; cursor: pointer;
      box-shadow: 0 4px 20px rgba(0,0,0,0.35);
      display: flex; align-items: center; justify-content: center;
      font-size: 24px; transition: transform 0.2s, box-shadow 0.2s;
    }
    #cw-bubble:hover { transform: scale(1.08); box-shadow: 0 6px 28px rgba(0,0,0,0.45); }
    #cw-window {
      position: fixed; bottom: 92px; right: 24px; z-index: 99999;
      width: 360px; max-height: 520px; border-radius: 16px;
      background: #1e2130; border: 1px solid #2e3348;
      box-shadow: 0 12px 48px rgba(0,0,0,0.55);
      display: flex; flex-direction: column; overflow: hidden;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      transition: opacity 0.2s, transform 0.2s;
    }
    #cw-window.cw-hidden { opacity: 0; transform: translateY(12px); pointer-events: none; }
    #cw-header {
      padding: 14px 16px; background: ${color}; color: #fff;
      display: flex; align-items: center; gap: 10px; flex-shrink: 0;
    }
    #cw-avatar {
      width: 32px; height: 32px; border-radius: 50%;
      background: rgba(255,255,255,0.25);
      display: flex; align-items: center; justify-content: center;
      font-size: 14px; font-weight: 700; flex-shrink: 0;
    }
    #cw-bot-name { font-weight: 600; font-size: 15px; flex: 1; }
    #cw-close {
      background: none; border: none; color: rgba(255,255,255,0.8);
      cursor: pointer; font-size: 20px; line-height: 1; padding: 0;
      transition: color 0.15s;
    }
    #cw-close:hover { color: #fff; }
    #cw-messages {
      flex: 1; overflow-y: auto; padding: 16px 12px;
      display: flex; flex-direction: column; gap: 10px;
      scroll-behavior: smooth;
      -webkit-overflow-scrolling: touch;
    }
    #cw-messages::-webkit-scrollbar { width: 4px; }
    #cw-messages::-webkit-scrollbar-thumb { background: #3a3f55; border-radius: 4px; }
    .cw-msg {
      max-width: 82%; padding: 9px 13px; border-radius: 14px;
      font-size: 14px; line-height: 1.5; word-wrap: break-word;
      white-space: pre-wrap; overflow-wrap: break-word;
    }
    .cw-msg-user {
      align-self: flex-end; background: ${color}; color: #fff;
      border-bottom-right-radius: 4px;
    }
    .cw-msg-assistant {
      align-self: flex-start; background: #252836; color: #e2e8f0;
      border-bottom-left-radius: 4px;
    }
    .cw-typing {
      align-self: flex-start; background: #252836; color: #8b92a9;
      padding: 9px 14px; border-radius: 14px; border-bottom-left-radius: 4px;
      font-size: 13px; display: flex; align-items: center; gap: 4px;
    }
    .cw-dot {
      width: 6px; height: 6px; border-radius: 50%; background: #8b92a9;
      animation: cw-bounce 1.2s infinite;
    }
    .cw-dot:nth-child(2) { animation-delay: 0.2s; }
    .cw-dot:nth-child(3) { animation-delay: 0.4s; }
    @keyframes cw-bounce {
      0%, 80%, 100% { transform: translateY(0); }
      40% { transform: translateY(-5px); }
    }
    #cw-footer {
      display: flex; gap: 8px; padding: 12px;
      border-top: 1px solid #2e3348; flex-shrink: 0;
      background: #1a1d27;
      /* iOS safe area for bottom notch */
      padding-bottom: max(12px, env(safe-area-inset-bottom));
    }
    #cw-input {
      flex: 1; background: #252836; border: 1px solid #2e3348;
      border-radius: 10px; color: #f1f3f9; font-size: 16px;
      padding: 9px 12px; outline: none; resize: none;
      font-family: inherit; transition: border-color 0.15s;
      max-height: 100px; overflow-y: auto;
      /* Prevent iOS zoom on focus (needs >= 16px) */
      -webkit-appearance: none;
    }
    #cw-input:focus { border-color: ${color}; }
    #cw-input::placeholder { color: #565d75; }
    #cw-send {
      background: ${color}; border: none; border-radius: 10px;
      width: 38px; min-width: 38px; height: 38px; min-height: 38px;
      cursor: pointer; flex-shrink: 0;
      display: flex; align-items: center; justify-content: center;
      transition: opacity 0.15s; align-self: flex-end;
      -webkit-appearance: none;
    }
    #cw-send:hover { opacity: 0.85; }
    #cw-send:disabled { opacity: 0.4; cursor: not-allowed; }
    #cw-send svg { width: 18px; height: 18px; fill: #fff; }
    .cw-msg-error {
      align-self: flex-start; background: #2d1b1b; color: #f87171;
      border: 1px solid #5c2020; border-bottom-left-radius: 4px;
      font-size: 13px;
    }
    .cw-retry-btn {
      display: inline-block; margin-top: 6px; padding: 4px 10px;
      background: #3d1f1f; border: 1px solid #f87171; border-radius: 6px;
      color: #f87171; font-size: 12px; cursor: pointer;
      transition: background 0.15s;
    }
    .cw-retry-btn:hover { background: #5c2020; }

    /* ── Mobile fullscreen + iOS keyboard fix ── */
    @media (max-width: 500px) {
      #cw-window {
        bottom: 0 !important; right: 0 !important; left: 0 !important;
        width: 100% !important; height: 100dvh !important;
        max-height: 100dvh !important;
        border-radius: 0 !important;
      }
    }
  `
  const style = document.createElement('style')
  style.textContent = css
  document.head.appendChild(style)
}

// ─────────────────────────────────────────────────────
// Build DOM
// ─────────────────────────────────────────────────────
function buildUI(config: AgentConfig, agentId: string) {
  // Bubble
  const bubble = el<HTMLButtonElement>('button')
  bubble.id = 'cw-bubble'
  bubble.setAttribute('aria-label', 'Abrir chat')
  bubble.textContent = '💬'

  // Window
  const win = el('div')
  win.id = 'cw-window'
  win.classList.add('cw-hidden')

  // Header
  const header = el('div')
  header.id = 'cw-header'

  const avatar = el('div')
  avatar.id = 'cw-avatar'
  avatar.textContent = config.botDisplayName.charAt(0).toUpperCase()

  const botName = el('span')
  botName.id = 'cw-bot-name'
  botName.textContent = config.botDisplayName

  const closeBtn = el<HTMLButtonElement>('button')
  closeBtn.id = 'cw-close'
  closeBtn.setAttribute('aria-label', 'Cerrar chat')
  closeBtn.textContent = '×'

  header.appendChild(avatar)
  header.appendChild(botName)
  header.appendChild(closeBtn)

  // Messages
  const messages = el('div')
  messages.id = 'cw-messages'

  // Footer
  const footer = el('div')
  footer.id = 'cw-footer'

  const input = el<HTMLTextAreaElement>('textarea')
  input.id = 'cw-input'
  input.setAttribute('placeholder', 'Escribe un mensaje...')
  input.setAttribute('rows', '1')

  const sendBtn = el<HTMLButtonElement>('button')
  sendBtn.id = 'cw-send'
  sendBtn.setAttribute('aria-label', 'Enviar')
  sendBtn.innerHTML = `<svg viewBox="0 0 24 24"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/></svg>`

  footer.appendChild(input)
  footer.appendChild(sendBtn)

  win.appendChild(header)
  win.appendChild(messages)
  win.appendChild(footer)

  document.body.appendChild(bubble)
  document.body.appendChild(win)

  // ── Welcome message
  if (config.welcomeMessage) {
    appendMessage('assistant', config.welcomeMessage, messages)
  }

  // ── Toggle open/close
  function openChat() {
    isOpen = true
    win.classList.remove('cw-hidden')
    bubble.textContent = '×'
    input.focus()
  }

  function closeChat() {
    isOpen = false
    win.classList.add('cw-hidden')
    bubble.textContent = '💬'
  }

  bubble.addEventListener('click', () => (isOpen ? closeChat() : openChat()))
  closeBtn.addEventListener('click', closeChat)

  // ── Send message
  async function handleSend() {
    const text = input.value.trim()
    if (!text || !sessionId) return

    input.value = ''
    input.style.height = 'auto'
    sendBtn.disabled = true
    input.disabled = true

    appendMessage('user', text, messages)
    const typingEl = appendTyping(messages)

    // Streaming assistant bubble
    const assistantBubble = el('div', 'cw-msg cw-msg-assistant')
    let started = false
    let fullStreamText = ''

    try {
      await sendMessage({
        apiBase,
        agentId,
        sessionId: sessionId!,
        conversationId,
        message: text,
        onDelta(chunk) {
          if (!started) {
            typingEl.remove()
            messages.appendChild(assistantBubble)
            started = true
          }
          // Accumulate raw text, render with formatting
          fullStreamText += chunk
          assistantBubble.innerHTML = formatMessage(fullStreamText)
          messages.scrollTop = messages.scrollHeight
        },
        onDone(_, cid) {
          if (!started) {
            typingEl.remove()
          }
          conversationId = cid
          messages.scrollTop = messages.scrollHeight
        },
        onError(errMsg) {
          typingEl.remove()
          if (!started) {
            showErrorBubble(errMsg, text, messages)
          }
        },
      })
    } catch (err) {
      typingEl.remove()
      if (!started) {
        showErrorBubble('Error al conectar. Intenta nuevamente.', text, messages)
      }
      console.error('[ChatWidget]', err)
    } finally {
      sendBtn.disabled = false
      input.disabled = false
      input.focus()
    }
  }

  sendBtn.addEventListener('click', handleSend)

  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  })

  // Auto-resize textarea
  input.addEventListener('input', () => {
    input.style.height = 'auto'
    input.style.height = Math.min(input.scrollHeight, 100) + 'px'
  })
}

function appendMessage(
  role: 'user' | 'assistant',
  text: string,
  container: HTMLElement
): HTMLElement {
  const div = el('div', `cw-msg cw-msg-${role}`)
  // User messages: plain text is fine. Assistant: format with line breaks
  if (role === 'assistant') {
    div.innerHTML = formatMessage(text)
  } else {
    div.textContent = text
  }
  container.appendChild(div)
  container.scrollTop = container.scrollHeight
  return div
}

function showErrorBubble(
  errorText: string,
  originalMessage: string,
  container: HTMLElement
) {
  const wrap = el('div', 'cw-msg cw-msg-error')
  const msg = el('span')
  msg.textContent = '⚠️ ' + errorText
  const retryBtn = el<HTMLButtonElement>('button', 'cw-retry-btn')
  retryBtn.textContent = '↻ Reintentar'
  retryBtn.onclick = () => {
    wrap.remove()
    const input = document.getElementById('cw-input') as HTMLTextAreaElement | null
    if (input) {
      input.value = originalMessage
      input.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', bubbles: true }))
    }
  }
  wrap.appendChild(msg)
  wrap.appendChild(document.createElement('br'))
  wrap.appendChild(retryBtn)
  container.appendChild(wrap)
  container.scrollTop = container.scrollHeight
}

function appendTyping(container: HTMLElement): HTMLElement {
  const typing = el('div', 'cw-typing')
  for (let i = 0; i < 3; i++) {
    const dot = el('div', 'cw-dot')
    typing.appendChild(dot)
  }
  container.appendChild(typing)
  container.scrollTop = container.scrollHeight
  return typing
}

// ─────────────────────────────────────────────────────
// iOS Visual Viewport fix — keeps footer visible
// when the virtual keyboard opens
// ─────────────────────────────────────────────────────
function setupIOSKeyboardFix() {
  const vv = window.visualViewport
  if (!vv) return // Not supported (older browsers)

  function adjustForKeyboard() {
    const win = document.getElementById('cw-window')
    if (!win || win.classList.contains('cw-hidden')) return

    // On mobile when keyboard is open, visualViewport.height < window.innerHeight
    const keyboardHeight = window.innerHeight - vv!.height
    if (keyboardHeight > 100) {
      // Keyboard is open
      win.style.height = `${vv!.height}px`
      win.style.maxHeight = `${vv!.height}px`
      win.style.bottom = '0px'
    } else {
      // Keyboard is closed — reset to CSS defaults
      win.style.height = ''
      win.style.maxHeight = ''
      win.style.bottom = ''
    }

    // Ensure messages scroll to bottom
    const msgs = document.getElementById('cw-messages')
    if (msgs) msgs.scrollTop = msgs.scrollHeight
  }

  vv.addEventListener('resize', adjustForKeyboard)
  vv.addEventListener('scroll', adjustForKeyboard)
}

// ─────────────────────────────────────────────────────
// Init
// ─────────────────────────────────────────────────────
export async function initWidget(agentId: string, resolvedApiBase: string) {
  apiBase = resolvedApiBase || window.location.origin


  // Fetch agent config (public endpoint, no auth)
  let config: AgentConfig
  try {
    const res = await fetch(`${apiBase}/api/agents/${agentId}/public`)
    if (!res.ok) {
      console.error('[ChatWidget] Agent not found or inactive.')
      return
    }
    config = await res.json()
  } catch (err) {
    console.error('[ChatWidget] Failed to load agent config:', err)
    return
  }

  // Init or restore session
  const stored = getOrCreateSession(agentId)
  if (stored) {
    sessionId = stored
  } else {
    try {
      const res = await fetch(`${apiBase}/api/chat/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ agentId }),
      })
      if (res.ok) {
        const data = await res.json()
        sessionId = data.sessionId
        saveSession(agentId, sessionId!)
      }
    } catch (err) {
      console.error('[ChatWidget] Failed to create session:', err)
    }
  }

  injectStyles(config.colorPrimary ?? '#6366f1')
  buildUI(config, agentId)
  setupIOSKeyboardFix()
}
